import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Search, User } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { Task, tasksService, TaskStatus } from '../../services/tasksService';
import { statusBadgeCss, severityBadgeCss } from '../../utils/statusVariant';
import NoDataFound from '../common/NoDataFound';
import TaskDetailModal from './investigation/TaskDetailModal';
import { usePermission } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

const ACT_REMEDIATIONS = 'CAP_REMEDIATIONS';

const RemediationsTab: React.FC = () => {
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_REMEDIATIONS, 'view');
    const { formatDate } = useEntitySettings();
    const fmtDate = (d?: string | null) => (d ? formatDate(d) : '');
    const [tasks, setTasks] = useState<Task[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<TaskStatus | 'OPEN_ALL' | ''>('OPEN_ALL');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [detailTaskId, setDetailTaskId] = useState<number | null>(null);

    const load = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const { data, total } = await tasksService.listPaged({
                source_type_code: 'CAPA_ISSUE',
                q: search || undefined,
                status: statusFilter && statusFilter !== 'OPEN_ALL' ? (statusFilter as TaskStatus) : undefined,
                open_only: statusFilter === 'OPEN_ALL',
                limit: pageSize,
                offset: (page - 1) * pageSize,
            });
            setTasks(data);
            setTotalRecords(total);
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to load remedial actions' });
        } finally {
            setLoading(false);
        }
    }, [search, statusFilter, page, pageSize, canView]);

    useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);
    useEffect(() => { load(); }, [load]);

    const today = new Date(); today.setHours(0, 0, 0, 0);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to CAPA remediations." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">

            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={statusFilter}
                        onChange={v => setStatusFilter((v ?? 'OPEN_ALL') as any)}
                        className="filter-status"
                        data-testid="SEL_capa_remediation_status_filter"
                        noSearchIcon
                        options={[
                            { value: 'OPEN_ALL', label: 'Open (all)' },
                            { value: 'OPEN', label: 'Open' },
                            { value: 'IN_PROGRESS', label: 'In progress' },
                            { value: 'DONE', label: 'Done' },
                            { value: 'VERIFIED', label: 'Verified' },
                            { value: 'REJECTED', label: 'Rejected' },
                            { value: '', label: 'All' },
                        ]}
                    />
                    <FormControl.Input
                        type="text"
                        placeholder="Search remedial actions…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_capa_remediation_search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Name</th>
                                        <th className="table-list-th">Owner</th>
                                        <th className="table-list-th">Due Date</th>
                                        <th className="table-list-th table-list-cell--priority">Priority</th>
                                        <th className="table-list-th table-list-cell--status">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'sm', 'xs', 'pill', 'pill']} />}
                                    {!loading && tasks.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="table-list-cell">
                                                <NoDataFound
                                                    title={search || statusFilter !== 'OPEN_ALL' ? 'No Matching Actions' : 'No Remedial Actions Found'}
                                                    message={search || statusFilter !== 'OPEN_ALL' ? 'No remedial actions match your search or filter.' : 'Create an action from a CAPA issue in the Issue Triage tab.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && tasks.map((t, idx) => {
                                        const dueOverdue = t.due_date && new Date(t.due_date) < today
                                            && !['DONE', 'VERIFIED', 'REJECTED', 'CANCELLED'].includes(t.status);
                                        return (
                                            <tr
                                                key={t.id}
                                                className="table-list-row cursor-pointer"
                                                onClick={() => setDetailTaskId(t.id)}
                                                data-testid={`BTN_capa_remediation_row_${t.id}`}
                                            >
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">{t.name}</span>
                                                    {t.description && (
                                                        <span className="table-list-cell-sub">{t.description}</span>
                                                    )}
                                                </td>
                                                <td className="table-list-cell">
                                                    {t.doer_name && (
                                                        <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                                                            <User size={11} />
                                                            {t.doer_name}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="table-list-cell">
                                                    {t.due_date && (
                                                        <span className={`inline-flex items-center gap-1 text-xs ${dueOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                                                            <Calendar size={11} />
                                                            {fmtDate(t.due_date)}
                                                            {dueOverdue && <span className="text-red-500">(overdue)</span>}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="table-list-cell">
                                                    {t.priority && (
                                                        <span className={severityBadgeCss(t.priority, 'sm')}>
                                                            {t.priority.replace(/\b\w/g, c => c.toUpperCase())}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className={statusBadgeCss(t.status, 'sm')}>
                                                        {t.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            pageSizeOptions={[10, 25, 50, 100]}
                            recordLabel="actions"
                        />
                    </div>
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
            <TaskDetailModal
                isOpen={!!detailTaskId}
                taskId={detailTaskId}
                frozen={false}
                onClose={() => setDetailTaskId(null)}
                onChanged={load}
            />
        </div>
    );
};

export default RemediationsTab;
